import type { PhoneTree } from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import {
  DialOptionType,
  type FetchPhoneTreeAnalyticsMultiTenantRequest,
  type GetPhoneTreeAnalyticsResponse_DialOption,
  type GetPhoneTreeAnalyticsResponse_PhoneTreeAnalytics,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-path/v1/api.pb';
import { buildDemoPhoneNumberId, buildDemoRoutingId, getDemoRoutingIdIndex } from './demo-ids';

// Pick only the fields these mocks return so new required backend fields don't break type-checking.
type DemoPhoneTree = Pick<PhoneTree, 'tenantId' | 'id' | 'name' | 'locationIds' | 'callRouteData'>;
type DemoDialOption = Pick<GetPhoneTreeAnalyticsResponse_DialOption, 'type' | 'dialOptionNumber' | 'totalCalls'>;
type DemoPhoneTreeAnalytics = Pick<
  GetPhoneTreeAnalyticsResponse_PhoneTreeAnalytics,
  | 'phoneTreeId'
  | 'phoneTreeName'
  | 'totalCalls'
  | 'abandonedCalls'
  | 'createdAt'
  | 'updatedAt'
  | 'callRouteId'
  | 'callRouteName'
  | 'officePhoneNumberIds'
> & { dialOptions: DemoDialOption[] };

interface DialOptionTemplate {
  type: DialOptionType;
  dialOptionNumber: string;
  totalCalls: number;
}

interface PhoneTreeTemplate {
  name: string;
  callRouteName: string;
  phoneNumberIndexes: number[];
  dialOptions: DialOptionTemplate[];
  abandonedCalls: number;
}

const numberOption = (dialOptionNumber: string, totalCalls: number): DialOptionTemplate => ({
  type: DialOptionType.DIAL_OPTION_NUMBER,
  dialOptionNumber,
  totalCalls,
});

const fallbackOption = (totalCalls: number): DialOptionTemplate => ({
  type: DialOptionType.DIAL_OPTION_FALLBACK,
  dialOptionNumber: '',
  totalCalls,
});

const extensionOption = (totalCalls: number): DialOptionTemplate => ({
  type: DialOptionType.DIAL_OPTION_EXTENSION,
  dialOptionNumber: '',
  totalCalls,
});

// More than 6 templates so the "View All" action is available in the demo.
const PHONE_TREE_TEMPLATES: PhoneTreeTemplate[] = [
  {
    name: 'Main Line Greeting',
    callRouteName: 'Main Line Route',
    phoneNumberIndexes: [1],
    dialOptions: [
      numberOption('1', 312),
      numberOption('2', 241),
      numberOption('3', 128),
      numberOption('4', 74),
      fallbackOption(56),
    ],
    abandonedCalls: 38,
  },
  {
    name: 'After Hours Menu',
    callRouteName: 'After Hours Route',
    phoneNumberIndexes: [1, 2],
    dialOptions: [numberOption('1', 96), numberOption('2', 61), fallbackOption(24)],
    abandonedCalls: 17,
  },
  {
    name: 'Appointment Line',
    callRouteName: 'Appointments Route',
    phoneNumberIndexes: [3],
    dialOptions: [numberOption('1', 204), numberOption('2', 133), numberOption('3', 87), numberOption('4', 41)],
    abandonedCalls: 22,
  },
  {
    name: 'New Patient Line',
    callRouteName: 'New Patients Route',
    phoneNumberIndexes: [4],
    dialOptions: [numberOption('1', 118), numberOption('2', 92), extensionOption(35)],
    abandonedCalls: 12,
  },
  {
    name: 'Billing & Insurance',
    callRouteName: 'Billing Route',
    phoneNumberIndexes: [5],
    dialOptions: [numberOption('1', 83), numberOption('2', 57), numberOption('3', 29)],
    abandonedCalls: 9,
  },
  {
    name: 'Emergency Line',
    callRouteName: 'Emergency Route',
    phoneNumberIndexes: [6],
    dialOptions: [numberOption('1', 45), numberOption('2', 18)],
    abandonedCalls: 4,
  },
  {
    name: 'Spanish Line',
    callRouteName: 'Spanish Route',
    phoneNumberIndexes: [7, 8],
    dialOptions: [numberOption('1', 52), numberOption('2', 31), numberOption('3', 14)],
    abandonedCalls: 6,
  },
];

export const communicationPhoneTreesDummyData = (
  tenantIds: string[]
): { phoneTrees: Record<string, { phoneTrees: DemoPhoneTree[] }> } => ({
  phoneTrees: Object.fromEntries(
    tenantIds.map((tenantId) => [
      tenantId,
      {
        phoneTrees: PHONE_TREE_TEMPLATES.map((template, idx) => ({
          tenantId,
          id: buildDemoRoutingId('phone-tree', tenantId, idx),
          name: template.name,
          locationIds: [],
          callRouteData: {
            id: buildDemoRoutingId('call-route', tenantId, idx),
            name: template.callRouteName,
          },
        })),
      },
    ])
  ),
});

const CREATED_AT = new Date(Date.now() - 1000 * 60 * 60 * 24 * 180).toISOString();

export const communicationPhoneTreeAnalyticsDummyData = (
  payload: FetchPhoneTreeAnalyticsMultiTenantRequest
): { phoneTreeAnalytics: DemoPhoneTreeAnalytics[] } => {
  const phoneTreeAnalytics = Object.entries(payload.tenantPhoneTreeIdsMap ?? {}).flatMap(
    ([tenantId, { phoneTreeIds }]) =>
      phoneTreeIds.flatMap((phoneTreeId): DemoPhoneTreeAnalytics[] => {
        const idx = getDemoRoutingIdIndex(phoneTreeId);
        const template = PHONE_TREE_TEMPLATES[idx];
        if (!template) return [];

        const dialOptions = [
          ...template.dialOptions,
          {
            type: DialOptionType.DIAL_OPTION_ABANDONED,
            dialOptionNumber: '',
            totalCalls: template.abandonedCalls,
          },
        ];

        return [
          {
            phoneTreeId,
            phoneTreeName: template.name,
            totalCalls: dialOptions.reduce((sum, option) => sum + option.totalCalls, 0),
            abandonedCalls: template.abandonedCalls,
            createdAt: CREATED_AT,
            updatedAt: CREATED_AT,
            dialOptions,
            callRouteId: buildDemoRoutingId('call-route', tenantId, idx),
            callRouteName: template.callRouteName,
            officePhoneNumberIds: template.phoneNumberIndexes.map(buildDemoPhoneNumberId),
          },
        ];
      })
  );

  return { phoneTreeAnalytics };
};
